import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../lib/axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Users,
  Package,
  ShoppingCart,
  Activity,
  IndianRupee,
  Globe,
  Store,
  AlertTriangle,
  BarChart3
} from 'lucide-react';
import { formatPrice } from '../../lib/utils';
import LoadingSpinner from '../../components/LoadingSpinner';
import { toast } from 'sonner';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total_users: 0,
    total_products: 0,
    total_orders: 0,
    total_revenue: 0,
    online_orders: 0,
    online_revenue: 0,
    pos_orders: 0,
    pos_revenue: 0,
    abandoned_carts: 0
  });
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`${API_URL}/api/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch admin stats:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Revenue',
      value: formatPrice(stats.total_revenue || 0),
      icon: IndianRupee,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-100',
      subtitle: `Online + POS combined`
    },
    {
      title: 'Total Orders',
      value: (stats.total_orders || 0).toLocaleString(),
      icon: ShoppingCart,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      subtitle: `${stats.online_orders || 0} online + ${stats.pos_orders || 0} POS`
    },
    {
      title: 'Customers',
      value: (stats.total_users || 0).toLocaleString(),
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      subtitle: 'Registered users'
    },
    {
      title: 'Live Products',
      value: (stats.total_products || 0).toLocaleString(),
      icon: Package,
      color: 'text-amber-600',
      bgColor: 'bg-amber-100',
      subtitle: 'Active in catalog'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-600 mt-2">
                Overview of your ecommerce platform
              </p>
            </div>
            
            <div className="flex items-center space-x-2">
              <Activity className="w-5 h-5 text-green-500" />
              <span className="text-sm text-gray-600">Live</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className="stat-card">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
                      <p
                        className="text-3xl font-bold text-gray-900 truncate"
                        data-testid={`stat-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {stat.value}
                      </p>
                      <p className="text-xs text-gray-500 mt-2 truncate">{stat.subtitle}</p>
                    </div>
                    <div className={`w-12 h-12 ${stat.bgColor} rounded-xl flex items-center justify-center shrink-0 ml-3`}>
                      <Icon className={`w-6 h-6 ${stat.color}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Online vs POS Breakdown + Abandoned Carts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <Card
            className="border-l-4 border-l-emerald-500 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/admin/orders')}
            data-testid="dashboard-online-card"
          >
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-2 text-emerald-700">
                <Globe className="w-5 h-5" /> <span className="font-semibold">Online Orders</span>
              </div>
              <p className="text-3xl font-bold">{stats.online_orders || 0}</p>
              <p className="text-sm text-gray-500 mt-1">
                {formatPrice(stats.online_revenue || 0)} revenue
              </p>
            </CardContent>
          </Card>

          <Card
            className="border-l-4 border-l-amber-500 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/admin/pos/orders')}
            data-testid="dashboard-pos-card"
          >
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-2 text-amber-700">
                <Store className="w-5 h-5" /> <span className="font-semibold">POS Walk-in Sales</span>
              </div>
              <p className="text-3xl font-bold">{stats.pos_orders || 0}</p>
              <p className="text-sm text-gray-500 mt-1">
                {formatPrice(stats.pos_revenue || 0)} revenue
              </p>
            </CardContent>
          </Card>

          <Card
            className={`border-l-4 ${
              (stats.abandoned_carts || 0) > 0 ? 'border-l-red-500' : 'border-l-gray-300'
            } cursor-pointer hover:shadow-md transition-shadow`}
            onClick={() => navigate('/admin/orders')}
            data-testid="dashboard-abandoned-card"
          >
            <CardContent className="p-5">
              <div className={`flex items-center gap-2 mb-2 ${
                (stats.abandoned_carts || 0) > 0 ? 'text-red-700' : 'text-gray-600'
              }`}>
                <AlertTriangle className="w-5 h-5" />
                <span className="font-semibold">Abandoned Carts</span>
              </div>
              <p className="text-3xl font-bold">{stats.abandoned_carts || 0}</p>
              <p className="text-sm text-gray-500 mt-1">
                {(stats.abandoned_carts || 0) > 0
                  ? 'Customers who started checkout but did not pay'
                  : 'No incomplete checkouts'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions + Insights link */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-emerald-600" />
                Deep Analytics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4 text-sm">
                Charts, top products, customer trends, daily revenue split between Online and POS.
              </p>
              <button
                onClick={() => navigate('/admin/analytics')}
                className="w-full px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium"
                data-testid="dashboard-open-analytics"
              >
                Open Analytics
              </button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <button 
                  onClick={() => navigate('/admin/products')}
                  className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors group"
                >
                  <Package className="w-8 h-8 text-gray-400 group-hover:text-blue-500 mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-700 group-hover:text-blue-700">Add Product</p>
                </button>
                
                <button 
                  onClick={() => navigate('/admin/customers')}
                  className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors group"
                >
                  <Users className="w-8 h-8 text-gray-400 group-hover:text-green-500 mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-700 group-hover:text-green-700">View Users</p>
                </button>
                
                <button 
                  onClick={() => navigate('/admin/orders')}
                  className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors group"
                >
                  <ShoppingCart className="w-8 h-8 text-gray-400 group-hover:text-purple-500 mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-700 group-hover:text-purple-700">Manage Orders</p>
                </button>
                
                <button 
                  onClick={() => navigate('/admin/analytics')}
                  className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-colors group"
                >
                  <Activity className="w-8 h-8 text-gray-400 group-hover:text-orange-500 mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-700 group-hover:text-orange-700">Analytics</p>
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
